/**
 * #5734 fix round (2026-08-31, round-3 CTO order): extracted from
 * metric-registry.ts's FINANCE_SEMANTIC_LAYER_REGISTRY_SEED to bring that
 * file back under the 2,600-line law after the measure-identity
 * declaration wave. Pure data relocation — no logic change, no row
 * touched beyond the file move itself. Same shape as the sibling
 * *.data.ts files (metric-registry.sga-spend-existing.data.ts,
 * metric-registry.ma-deal.data.ts): a typed array of raw seed-input
 * objects the main file maps through metricEntry() and splices back into
 * FINANCE_SEMANTIC_LAYER_REGISTRY_SEED at the exact position it came
 * from, so the compiled registry, its row order, and every exported
 * symbol are unchanged.
 *
 * Contains six single-row decision-lever operating-income-contribution
 * outputs: capital-allocation's capital_freed_usd, and five
 * carrier-transportation-cost / churn-reduction contribution rows
 * (carrier_cost_opportunity_usd, carrier_exit_net_margin_impact_usd,
 * churn_reduction_operating_income_contribution_usd,
 * mode_shift_margin_impact_usd, network_reconfiguration_margin_impact_usd).
 */
import type { MetricEntrySeedInput } from './metric-registry';

export const FINANCE_LEVER_CONTRIBUTION_METRIC_REGISTRY_SEED_INPUTS_A: readonly MetricEntrySeedInput[] =
  Object.freeze([
    {
      metricKey: 'capital_freed_usd',
      measureIdentity: { measuredDimension: 'other', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'Capital Allocation Decision: capital_freed_usd',
      definition:
        'Governed cash-track contribution the capital-allocation module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, #4009 wave-2C contract census 2026-07-27).',
      reconciliationRule:
        'Recomputed by capex_reprioritization_value_v1 in the owning module; CASH-track — the manifest contract targets cash_flow_release@objective_value_bridge_v1 and the registry has not adjudicated freed capital into the cash bridge (cash_flow_release allowedLeverBranches deliberately excludes capital-allocation), so this seed entry emits no targetMetricRef and the claim never enters an operating-income bridge.',
      source:
        'apps/api/src/decision-modules/capital-allocation/capital-allocation.manifest.ts#contributionClaimId:capital_freed_usd',
      registryLayer: 'core',
      unit: 'USD',
      temporalType: 'flow',
      direction: 'higher_is_better',
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'capital-allocation',
      sourceBindingKeys: ['capital_freed_usd'],
      requiredEvidenceClasses: ['module_composite_contribution_v1'],
    },
    {
      metricKey: 'carrier_cost_opportunity_usd',
      measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'Transportation Cost & Margin Analysis: carrier_cost_opportunity_usd',
      definition:
        'Governed decision-lever contribution the carrier-transportation-cost module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
      reconciliationRule:
        'Recomputed from the observed carrier baseline by carrier_transportation_cost_opportunity@1.0.3 in the owning module; MODELED option receipts from @1.1.0 remain scenario-only and never replace or sum into this aggregate contribution.',
      source:
        'apps/api/src/decision-modules/carrier-transportation-cost/carrier-transportation-cost.manifest.ts#contributionClaimId:carrier_cost_opportunity_usd',
      registryLayer: 'core',
      unit: 'USD',
      temporalType: 'flow',
      direction: 'higher_is_better',
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'carrier-transportation-cost',
      sourceBindingKeys: ['carrier_cost_opportunity_usd'],
      targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
      requiredEvidenceClasses: ['module_composite_contribution_v1'],
    },
    {
      metricKey: 'carrier_exit_net_margin_impact_usd',
      measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'Transportation Cost & Margin Analysis: carrier_exit_net_margin_impact_usd',
      definition:
        'Governed decision-lever contribution the carrier-transportation-cost module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
      reconciliationRule:
        'Recomputed by the approved formula carrier_exit_tradeoff_v1, executed by the size_carrier_exit_tradeoff operation in the owning module; rolls into its target only through the objective-value bridge, never summed twice.',
      source:
        'apps/api/src/decision-modules/carrier-transportation-cost/carrier-transportation-cost.manifest.ts#contributionClaimId:carrier_exit_net_margin_impact_usd',
      registryLayer: 'core',
      unit: 'USD',
      temporalType: 'flow',
      direction: 'higher_is_better',
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'carrier-transportation-cost',
      sourceBindingKeys: ['carrier_exit_net_margin_impact_usd'],
      targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
      requiredEvidenceClasses: ['module_composite_contribution_v1'],
    },
    {
      metricKey: 'churn_reduction_operating_income_contribution_usd',
      measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'Churn Reduction Decision: churn_reduction_operating_income_contribution_usd',
      definition:
        'Governed customer-retention contribution the churn-reduction module publishes as a candidate-only operating-income claim from certified event risk and approved save-play economics.',
      reconciliationRule:
        'Recomputed by churn_reduction_opportunity from event-based revenue at risk, approved save rate, approved operating-income conversion, and approved program cost; cohort attrition is not added, and same-scope revenue overlap requires a de-confliction receipt before composition.',
      source:
        'apps/api/src/decision-modules/churn-reduction/churn-reduction.manifest.ts#contributionClaimId:churn_reduction_operating_income_contribution_usd',
      registryLayer: 'core',
      unit: 'USD',
      temporalType: 'flow',
      direction: 'higher_is_better',
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'churn-reduction',
      sourceBindingKeys: ['churn_reduction_operating_income_contribution_usd'],
      targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
      requiredEvidenceClasses: ['module_composite_contribution_v1'],
    },
    {
      metricKey: 'mode_shift_margin_impact_usd',
      measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'Transportation Cost & Margin Analysis: mode_shift_margin_impact_usd',
      definition:
        'Governed decision-lever contribution the carrier-transportation-cost module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
      reconciliationRule:
        'Recomputed by the approved formula mode_shift_tradeoff_v1, executed by the size_mode_shift_tradeoff operation in the owning module; owns the dollar claim only — the carbon claim on the same physical shift belongs to mode_shift_emissions_v1 and is never summed with it; rolls into its target only through the objective-value bridge, never summed twice.',
      source:
        'apps/api/src/decision-modules/carrier-transportation-cost/carrier-transportation-cost.manifest.ts#contributionClaimId:mode_shift_margin_impact_usd',
      registryLayer: 'core',
      unit: 'USD',
      temporalType: 'flow',
      direction: 'higher_is_better',
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'carrier-transportation-cost',
      sourceBindingKeys: ['mode_shift_margin_impact_usd'],
      targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
      requiredEvidenceClasses: ['module_composite_contribution_v1'],
    },
    {
      metricKey: 'network_reconfiguration_margin_impact_usd',
      measureIdentity: { measuredDimension: 'operating_income', valueForm: 'amount', unitFamily: 'currency', normalizationBasis: 'none' },
      label: 'Transportation Cost & Margin Analysis: network_reconfiguration_margin_impact_usd',
      definition:
        'Governed decision-lever contribution the carrier-transportation-cost module publishes into the composite; registered by the semantic ontology as a derived metric (extract-do-not-invent, module audit book 2026-07-10).',
      reconciliationRule:
        'Recomputed by the approved formula specialist_network_option_score_v1, executed by the score_specialist_network_options operation in the owning module; the formula ranks admitted third-party optimizer options and republishes the admitted margin impact, it never re-optimizes the network; rolls into its target only through the objective-value bridge, never summed twice.',
      source:
        'apps/api/src/decision-modules/carrier-transportation-cost/carrier-transportation-cost.manifest.ts#contributionClaimId:network_reconfiguration_margin_impact_usd',
      registryLayer: 'core',
      unit: 'USD',
      temporalType: 'flow',
      direction: 'higher_is_better',
      benchmarkable: false,
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'carrier-transportation-cost',
      sourceBindingKeys: ['network_reconfiguration_margin_impact_usd'],
      targetMetricRef: { metricId: 'operating_income', registryVersion: 'objective_value_bridge_v1' },
      requiredEvidenceClasses: ['module_composite_contribution_v1'],
    },
  ]);
